import React, { FC, Suspense } from 'react';
import { RouteNotFoundError } from '@funtech-int/rival';
import { ErrorBoundary, ErrorFallbackProps } from '@funtech-int/rival-react';
import { HTTPError } from '@funtech-int/web-toolkit';

import { Button } from '@/shared/components/Button/Button';
import { TextWeight, TextSize, TextTag, Theme } from '@/shared/components/Text';
import { Text } from '@/shared/components/Text/Text';
import { HeaderLogo } from '@/shared/assets/HeaderLogo';
import { useI18n } from '@/shared/hooks/useI18n';
import { IntlProvider } from '@/app/components/IntlProvider/IntlProvider';

import { Link } from '../Link/Link';

import styles from './ErrorFallback.module.css';

interface ErrorFallbackContentProps {
    status: number;
    title: string;
    description: string;
    refreshButtonText: string;
    hideHeader?: boolean;
}

const isNotFoundError = (error: Error) =>
    error instanceof RouteNotFoundError || (error instanceof HTTPError && error.status === 404);

const ErrorFallbackContent: FC<ErrorFallbackContentProps> = ({
    status,
    title,
    description,
    refreshButtonText,
    hideHeader = false,
}) => (
    <div className={styles.root}>
        {!hideHeader && (
            <div className={styles.header}>
                <Link className={styles.headerLink} href="/">
                    <HeaderLogo />
                </Link>
            </div>
        )}
        <main className={styles.contentWrapper}>
            <div className={styles.content}>
                <div className={styles.headline}>
                    <Text weight={TextWeight.Bold} theme={Theme.Dark} size={TextSize.TitlePage}>
                        {status}
                    </Text>
                    <Text
                        weight={TextWeight.Bold}
                        tag={TextTag.H1}
                        theme={Theme.Dark}
                        size={TextSize.TitlePage}
                    >
                        {title}
                    </Text>
                </div>
                <Text
                    className={styles.description}
                    weight={TextWeight.Regular}
                    size={TextSize.Subtitle}
                >
                    {description}
                </Text>
                <div className={styles.buttons}>
                    <Button
                        size={52}
                        variant="primary"
                        theme="dark"
                        onClick={() => window.location.reload()}
                    >
                        {refreshButtonText}
                    </Button>
                </div>
            </div>
        </main>
    </div>
);

const LocalizedErrorFallbackContent: FC<ErrorFallbackProps & { hideHeader?: boolean }> = ({
    error,
    hideHeader,
}) => {
    const i18n = useI18n();
    const notFoundError = isNotFoundError(error);

    return (
        <ErrorFallbackContent
            hideHeader={hideHeader}
            status={notFoundError ? 404 : 500}
            title={notFoundError ? i18n('error__title') : i18n('error__server_title')}
            description={
                notFoundError ? i18n('error__description') : i18n('error__server_description')
            }
            refreshButtonText={i18n('error__refresh_page')}
        />
    );
};

const PlainErrorFallbackContent: FC<ErrorFallbackProps & { hideHeader?: boolean }> = ({
    error,
    hideHeader,
}) => {
    const notFoundError = isNotFoundError(error);

    return (
        <ErrorFallbackContent
            hideHeader={hideHeader}
            status={notFoundError ? 404 : 500}
            title={notFoundError ? 'Page not found' : 'Server error'}
            description={
                notFoundError
                    ? 'It may have been moved, or the page address might be incorrect'
                    : 'The website is currently unavailable. Try again later.'
            }
            refreshButtonText="Refresh the page"
        />
    );
};

export const ErrorFallback: FC<ErrorFallbackProps & { hideHeader?: boolean }> = (props) => {
    const fallback = <PlainErrorFallbackContent {...props} />;

    return (
        <ErrorBoundary fallback={fallback}>
            <Suspense fallback={fallback}>
                <IntlProvider>
                    <LocalizedErrorFallbackContent {...props} />
                </IntlProvider>
            </Suspense>
        </ErrorBoundary>
    );
};
